'use client';

import { FRESHCHAT_SCRIPT_ID, FRESHCHAT_WIDGET_URL } from './constants';
import { FreshChatInit, FreshchatEvents, FreshchatOpenPayload } from './types';

class FreshchatService {
  isLoading = false;

  freshChatEvents = (initConfig: FreshChatInit) => {
    window.fwcrm?.on(FreshchatEvents.widgetLoaded, () => {
      window.fcWidget?.on(FreshchatEvents.widgetDestroyed, () => {
        this.removeOldScript();
      });

      window.fcWidget?.on(FreshchatEvents.userCreated, (resp) => {
        const data = resp && resp.data;

        if (data?.restoreId && initConfig.setRestoreId) {
          initConfig.setRestoreId(data.restoreId);
        }
      });

      window.fcWidget?.user.get((response) => {
        if (initConfig.externalId) {
          window.fcWidget?.user.setProperties({
            email: initConfig.externalId,
            firstName: initConfig.externalId,
          });
        }

        if (
          !initConfig.restoreId &&
          response?.data &&
          response?.data?.restoreId &&
          initConfig.setRestoreId
        ) {
          initConfig.setRestoreId(response?.data.restoreId);
        }
      });
    });
  };

  init = async (initConfig: FreshChatInit) => {
    try {
      if (this.isInitialized || this.isLoading) {
        return;
      }

      this.isLoading = true;

      this.removeOldScript();
      window.fcWidgetMessengerConfig = {
        locale: 'en',
        externalId: initConfig.externalId,
        restoreId: initConfig.restoreId,
        config: {
          headerProperty: {
            hideChatButton: initConfig.hideChatButton ?? false,
          },
        },
      };

      const freshchatScript = document.createElement('script');

      freshchatScript.src = FRESHCHAT_WIDGET_URL;
      freshchatScript.id = FRESHCHAT_SCRIPT_ID;
      freshchatScript.setAttribute('chat', 'true');
      freshchatScript.onload = () => {
        this.freshChatEvents(initConfig);
      };
      const head = document.head || document.getElementsByTagName('head')[0];

      head.insertBefore(freshchatScript, head.firstChild);
    } catch (e) {
      return;
    } finally {
      this.isLoading = false;
    }
  };

  get isInitialized() {
    return window.fcWidget && window.fcWidget.isInitialized();
  }

  isWidgetLoaded = () => {
    return !!window.fcWidget && window.fcWidget.isLoaded();
  };

  isWidgetOpen = () => {
    return window.fcWidget && window.fcWidget.isOpen();
  };

  open = () => {
    if (window.fcWidget) {
      window.fcWidget.open();
    }
  };

  openWithParams = (payload?: FreshchatOpenPayload) => {
    if (window.fcWidget) {
      window.fcWidget.open(payload);
    }
  };

  close = () => {
    if (this.isWidgetOpen()) {
      window.fcWidget?.close();
    }
  };

  updateUser = async (externalId: string) => {
    await window.fcWidget?.user
      .update({
        email: externalId,
        firstName: externalId,
      })
      .catch((_) => null);
  };

  clearUser = async () => {
    try {
      if (window.fcWidget) {
        const isExists = await window.fcWidget.user.isExists();

        if (isExists.data === true) {
          await window.fcWidget.user.clear();
        }
      }

      return true;
    } catch (e) {
      return false;
    }
  };

  destroy = () => {
    if (!this.isInitialized) {
      return false;
    }

    window.fcWidget?.destroy();

    return true;
  };

  hide() {
    window.fcWidget?.hide();
  }

  show() {
    window.fcWidget?.show();
  }

  removeOldScript() {
    const freshchatScript = document.getElementById(FRESHCHAT_SCRIPT_ID);

    if (freshchatScript) {
      freshchatScript.remove();
    }
  }
}

const freshchatService = new FreshchatService();

export { freshchatService };
